import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SIGNUP_DOMAIN = (process.env.REACT_APP_PUBLIC_APP_URL || 'https://intellitix.ai').replace(/\/$/, '');
const referralLink = (affid) => `${SIGNUP_DOMAIN}/signup?affid=${affid}`;

export default function AffiliateDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [data, setData] = useState(null);
  
  // Date filters for referrals
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Activity Stats state
  const [activityStats, setActivityStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsStartDate, setStatsStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().split('T')[0];
  });
  const [statsEndDate, setStatsEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  
  // Password reset loading state
  const [resetLoadingId, setResetLoadingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [txById, setTxById] = useState({});
  const [txLoadingId, setTxLoadingId] = useState(null);

  const toggleTransactions = async (userId) => {
    if (expandedId === userId) { setExpandedId(null); return; }
    setExpandedId(userId);
    if (txById[userId]) return; // cached
    setTxLoadingId(userId);
    try {
      const res = await fetch(`${API_URL}/api/auth/affiliate/referral/${userId}/transactions`, { credentials: 'include' });
      const d = await res.json();
      if (res.ok) setTxById((m) => ({ ...m, [userId]: d }));
    } catch (e) { /* noop */ }
    finally { setTxLoadingId(null); }
  };

  // Check if user is an affiliate
  useEffect(() => {
    if (user && !user.is_affiliate) {
      navigate('/');
    }
  }, [user, navigate]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      let url = `${API_URL}/api/auth/affiliate/dashboard`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to fetch dashboard');
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (user?.is_affiliate) {
      fetchDashboard();
      fetchActivityStats();
    }
  }, [user, fetchDashboard]);

  // Fetch activity stats for affiliate's referrals
  const fetchActivityStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/quickpick/affiliate-stats?start_date=${statsStartDate}&end_date=${statsEndDate}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const result = await response.json();
        setActivityStats(result);
      }
    } catch (err) {
      console.error('Failed to fetch activity stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [statsStartDate, statsEndDate]);

  useEffect(() => {
    if (user?.is_affiliate) {
      fetchActivityStats();
    }
  }, [statsStartDate, statsEndDate, user, fetchActivityStats]);

  const handleFilter = (e) => {
    e.preventDefault();
    fetchDashboard();
  };

  // Download the marketing HTML email with this affiliate's tracked link embedded
  const handleDownloadEmail = async () => {
    const affid = data?.affiliate?.affid;
    if (!affid) return;
    setError(''); setSuccess('');
    try {
      const response = await fetch(`${API_URL}/api/auth/affiliates/${affid}/email-html`, {
        credentials: 'include',
      });
      if (!response.ok) {
        let msg = 'Failed to generate email';
        try { const d = await response.json(); if (d.detail) msg = typeof d.detail === 'string' ? d.detail : msg; } catch {}
        throw new Error(msg);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intellitix-affiliate-${affid}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('Your promo email was downloaded.');
    } catch (err) {
      setError(err.message);
    }
  };
  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
      trial: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      cancelled: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      expired: 'bg-red-500/20 text-red-400 border-red-500/50',
      none: 'bg-slate-500/20 text-slate-400 border-slate-500/50'
    };
    return badges[status] || badges.none;
  };

  // Force password reset for a referred user
  const handleFlagForSupport = async (referredUser) => {
    const reason = window.prompt(`Flag ${referredUser.email} for support?\n\nOptionally add a short note for the admin:`, '');
    if (reason === null) return; // cancelled

    setError('');
    setSuccess('');
    setResetLoadingId(referredUser.id);

    try {
      const response = await fetch(
        `${API_URL}/api/auth/affiliate/referral/${referredUser.id}/flag-support`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ reason }),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to flag for support');
      }
      setSuccess(result.message || `${referredUser.email} flagged for support.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setResetLoadingId(null);
    }
  };

  if (!user?.is_affiliate) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-2xl font-bold text-white">
              IntelliTix<span className="text-emerald-400">®</span>
            </Link>
            <span className="text-slate-500">|</span>
            <span className="text-amber-400 font-medium">Affiliate Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">{user.email}</span>
            <Link to="/" className="text-sm text-slate-400 hover:text-white transition">
              Back to App
            </Link>
            <button 
              onClick={logout}
              className="text-sm text-slate-400 hover:text-white transition"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-400 mt-4">Loading dashboard...</p>
          </div>
        ) : data ? (
          <>
            {/* Affiliate Info */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">
                Welcome, {data.affiliate?.name || data.affiliate?.affid}
              </h1>
              <p className="text-slate-400">
                Affiliate ID: <span className="text-amber-400 font-mono">{data.affiliate?.affid}</span>
                <span className="mx-2">•</span>
                Commission Rate: <span className="text-emerald-400">{data.affiliate?.commission_pct}%</span>
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <p className="text-slate-400 text-sm mb-1">Total Signups</p>
                <p className="text-3xl font-bold text-white">{data.stats?.total_signups || 0}</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <p className="text-slate-400 text-sm mb-1">Active Subscribers</p>
                <p className="text-3xl font-bold text-emerald-400">{data.stats?.active_subscribers || 0}</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <p className="text-slate-400 text-sm mb-1">Conversion Rate</p>
                <p className="text-3xl font-bold text-blue-400">{data.stats?.conversion_rate || 0}%</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <p className="text-slate-400 text-sm mb-1">Est. Commission</p>
                <p className="text-3xl font-bold text-amber-400">{formatCurrency(data.stats?.estimated_commission || 0)}</p>
              </div>
            </div>

            {/* Revenue Summary */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h2 className="text-lg font-semibold text-white mb-4">Revenue Summary</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-sm">Estimated Revenue (from your referrals)</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(data.stats?.estimated_revenue || 0)}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Your Commission ({data.affiliate?.commission_pct}%)</p>
                  <p className="text-2xl font-bold text-emerald-400">{formatCurrency(data.stats?.estimated_commission || 0)}</p>
                </div>
              </div>
            </div>

            {/* Activity Stats Card */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Referral Activity Stats</h2>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={statsStartDate}
                    onChange={(e) => setStatsStartDate(e.target.value)}
                    className="px-3 py-1.5 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-slate-500">to</span>
                  <input
                    type="date"
                    value={statsEndDate}
                    onChange={(e) => setStatsEndDate(e.target.value)}
                    className="px-3 py-1.5 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    onClick={fetchActivityStats}
                    disabled={statsLoading}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 text-white text-sm font-medium rounded-lg transition"
                  >
                    {statsLoading ? '...' : 'Apply'}
                  </button>
                </div>
              </div>
              
              {/* Totals */}
              {activityStats && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-900 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-emerald-400">{activityStats.total_logins}</div>
                      <div className="text-slate-400 text-sm">Logins by Your Referrals</div>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-amber-400">{activityStats.total_downloads}</div>
                      <div className="text-slate-400 text-sm">Downloads by Your Referrals</div>
                    </div>
                  </div>
                  
                  {/* Daily breakdown table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-600">
                          <th className="text-left py-2 px-3 text-slate-400 font-medium text-sm">Date</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-medium text-sm">Logins</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-medium text-sm">Downloads</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(activityStats.daily || [])]?.reverse().map((day) => (
                          <tr key={day.date} className="border-b border-slate-700 hover:bg-slate-800/50">
                            <td className="py-2 px-3 text-white text-sm">{day.date}</td>
                            <td className="py-2 px-3 text-right text-emerald-400 font-medium text-sm">{day.logins}</td>
                            <td className="py-2 px-3 text-right text-amber-400 font-medium text-sm">{day.downloads}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              
              {statsLoading && (
                <div className="text-center py-4 text-slate-400">Loading stats...</div>
              )}
            </div>

            {/* Date Filters */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-6">
              <h2 className="text-lg font-semibold text-white mb-4">Filter by Date</h2>
              <form onSubmit={handleFilter} className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition"
                >
                  Apply Filter
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition"
                >
                  Clear
                </button>
              </form>
            </div>

            {/* Referred Users Table */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-700">
                <h2 className="text-lg font-semibold text-white">Your Referrals</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Email</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Name</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Plan</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Signed Up</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-slate-300">Charges</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-slate-300">Commission</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {!data.users || data.users.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                          No referrals yet. Share your link: 
                          <code className="ml-2 px-2 py-1 bg-slate-800 rounded text-amber-400">
                            {referralLink(data.affiliate?.affid)}
                          </code>
                        </td>
                      </tr>
                    ) : (
                      data.users.map((u, i) => (
                        <React.Fragment key={u.id || i}>
                        <tr
                          className="hover:bg-slate-800/50 cursor-pointer"
                          onClick={() => toggleTransactions(u.id)}
                          data-testid={`referral-row-${u.id}`}
                        >
                          <td className="px-4 py-3 text-white">
                            <span className="inline-flex items-center gap-2">
                              <span className={`text-slate-500 transition-transform ${expandedId === u.id ? 'rotate-90' : ''}`}>▶</span>
                              {u.email}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-300">{u.name || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(u.subscription_status)}`}>
                              {u.subscription_status || 'none'}
                            </span>
                            {u.force_password_reset && (
                              <span className="ml-2 px-2 py-1 text-xs font-medium rounded border bg-amber-500/20 text-amber-400 border-amber-500/50">
                                pwd reset
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-300 text-sm">{u.subscription_plan || '-'}</td>
                          <td className="px-4 py-3 text-slate-400 text-sm">{formatDate(u.created_at)}</td>
                          <td className="px-4 py-3 text-right text-slate-300 text-sm">{u.charges ?? 0}</td>
                          <td className="px-4 py-3 text-right text-emerald-400 font-medium text-sm">${(u.commission ?? 0).toFixed(2)}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleFlagForSupport(u); }}
                              disabled={resetLoadingId === u.id}
                              data-testid={`flag-support-${u.id}`}
                              className="text-xs px-2 py-1 bg-sky-600/20 hover:bg-sky-600/40 disabled:bg-slate-700 disabled:cursor-not-allowed text-sky-300 disabled:text-slate-500 rounded transition"
                              title="Flag this referral for admin support (emails the team)"
                            >
                              {resetLoadingId === u.id ? '...' : 'Flag for Support'}
                            </button>
                          </td>
                        </tr>
                        {expandedId === u.id && (
                          <tr className="bg-slate-900/60" data-testid={`referral-transactions-${u.id}`}>
                            <td colSpan="8" className="px-6 py-3">
                              {txLoadingId === u.id ? (
                                <p className="text-slate-400 text-sm py-2">Loading transactions…</p>
                              ) : (txById[u.id]?.transactions?.length ? (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Itemized charges (newest first)</span>
                                    <span className="text-xs text-slate-400">
                                      {txById[u.id].totals.count} charges · Revenue ${txById[u.id].totals.revenue.toFixed(2)} · Commission <span className="text-emerald-400 font-semibold">${txById[u.id].totals.commission.toFixed(2)}</span>
                                    </span>
                                  </div>
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-slate-500 border-b border-slate-700">
                                        <th className="text-left py-1.5 font-medium">Date</th>
                                        <th className="text-right py-1.5 font-medium">Charge</th>
                                        <th className="text-right py-1.5 font-medium">Rate</th>
                                        <th className="text-right py-1.5 font-medium">Commission</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {txById[u.id].transactions.map((t) => (
                                        <tr key={t.invoice_id} className="border-b border-slate-800/60">
                                          <td className="py-1.5 text-slate-300">{formatDate(t.occurred_at)}</td>
                                          <td className="py-1.5 text-right text-slate-200">${(t.amount || 0).toFixed(2)}</td>
                                          <td className="py-1.5 text-right text-slate-400">{t.commission_pct_applied}%</td>
                                          <td className="py-1.5 text-right text-emerald-400 font-medium">${(t.commission_amount || 0).toFixed(2)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-slate-400 text-sm py-2">No charges recorded yet for this referral.</p>
                              ))}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Referral Link */}
            <div className="mt-8 bg-amber-500/10 border border-amber-500/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-400 mb-2">Your Referral Link</h3>
              <p className="text-slate-300 mb-4">Share this link to earn commissions on new signups:</p>
              <div className="flex gap-3">
                <code className="flex-1 px-4 py-3 bg-slate-900 rounded-lg text-amber-400 font-mono text-sm overflow-x-auto">
                  {referralLink(data.affiliate?.affid)}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(referralLink(data.affiliate?.affid));
                    alert('Link copied to clipboard!');
                  }}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition whitespace-nowrap"
                >
                  Copy Link
                </button>
              </div>
              <div className="mt-4 pt-4 border-t border-amber-500/30">
                <p className="text-slate-300 mb-3 text-sm">
                  Prefer to email your audience? Download a ready-to-send promo email with your link already embedded.
                </p>
                <button
                  onClick={handleDownloadEmail}
                  data-testid="download-affiliate-email"
                  className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white font-medium rounded-lg transition whitespace-nowrap"
                >
                  Download HTML Email
                </button>
              </div>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}
